import React, { FC, Fragment, PropsWithChildren, useState } from 'react';
import {
  TextInput,
  required,
  number,
  useTranslate,
  BooleanInput,
  NumberInput,
  SimpleForm,
  FormDataConsumer,
  useLocale,
} from 'react-admin';
import { useForm, useFormState } from 'react-final-form';

import { NumberEx } from '~/utils';
import ReferenceField from '../../../../components/ReferenceField';
import { isEmpty } from 'lodash';
import { Button, Dialog, DialogContent, Grid, IconButton } from '@material-ui/core';
import { BottomToolbar, H2, H3, TextField } from '~/components';
import DeleteIcon from '@material-ui/icons/Delete';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { ObjectType } from '~/types';
import cx from 'classnames';
import SolutionSelect from '../../../../components/ra/SolutionSelect';
import CurrencyAutocomplete from '../../../../components/ra/CurrencyAutocomplete';
import i18n from '~/i18n';
import lodashGet from 'lodash/get';

const ConfigurationForm = () => {
  const translate = useTranslate();
  const t = (key: string): string => translate(l(`fields.${key}`));
  const locale: keyof any = useLocale();
  const allCurrencies: { [key: string]: string } = (i18n as any)[locale].currencies;

  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [openIndividualSolutionDialog, setOpenIndividualSolutionDialog] = useState(false);
  const [openBusinessSolutionDialog, setOpenBusinessSolutionDialog] = useState(false);
  const [openTransitChannelDialog, setOpenTransitChannelDialog] = useState(false);
  const [openTransitDepositDialog, setOpenTransitDepositDialog] = useState(false);

  const form = useForm();
  const { values: formValues } = useFormState();
  const isExternalAccountLinkRegistrationEnabled =
    formValues?.externalAccountLinkRegistration?.enabled;
  const isAccountMirroringEnabled = formValues?.accountMirroring?.enabled;
  const isExternalProfileLinkSyncEnabled = formValues?.externalProfileLinkSync?.enabled;
  const isTransactionTransferMirroringEnabled = formValues?.transactionTransferMirroring?.enabled;
  const productsMapping: Record<string, string> = formValues?.productTypeKeys ?? {};
  const individualSolutionMapping: Record<string, string> = formValues?.clientRoleKeys ?? {};
  const businessSolutionMapping: Record<string, string> = formValues?.groupRoleKeys ?? {};
  const transitChannelMapping: Record<string, string> =
    formValues?.transactionTransferMirroring?.transitChannelKeys ?? {};
  const transitDepositMapping: Record<string, string> =
    formValues?.transactionTransferMirroring?.transitDepositKeys ?? {};

  const resetField = (source: string) => () => {
    form.change(source, undefined);
  };

  const onObjectAdd = (source: VeenguMappingKeys) => (newRecord: {
    veenguId: string;
    mambuId: string;
  }): void => {
    form.change(`${source}.${newRecord.veenguId}`, newRecord.mambuId);
    if (source === 'productTypeKeys') {
      setOpenProductDialog(false);
    } else if (source === 'clientRoleKeys') {
      setOpenIndividualSolutionDialog(false);
    } else if (source === 'groupRoleKeys') {
      setOpenBusinessSolutionDialog(false);
    } else if (source === 'transactionTransferMirroring.transitChannelKeys') {
      setOpenTransitChannelDialog(false);
    } else if (source === 'transactionTransferMirroring.transitDepositKeys') {
      setOpenTransitDepositDialog(false);
    } else {
    }
  };
  const onObjectDelete = (source: VeenguMappingKeys) => (veenguId: string): void => {
    form.change(`${source}.${veenguId}`, undefined);
  };

  const validateObjectAlreadyAdded = (source: VeenguMappingKeys) => () => (veenguId: string) => {
    if (lodashGet(formValues, source)?.hasOwnProperty(veenguId)) {
      return t('validations.alreadyAdded');
    }
    return undefined;
  };

  return (
    <Fragment>
      <H2 topSpacing={0}>{t('apiConfiguration')}</H2>
      <Grid container spacing={2}>
        <Grid item sm={sm}>
          <TextInput source='api.baseUrl' label={t('baseUrl')} validate={required()} />
        </Grid>
        <Grid item sm={sm}>
          <TextInput source='api.apiKey' label={t('apiKey')} validate={required()} fullWidth />
        </Grid>
      </Grid>
      <H2 topSpacing={0}>{t('basicConfiguration')}</H2>
      <Grid container spacing={2}>
        <Grid item sm={sm}>
          <NumberInput
            source='timeZoneOffset'
            label={t('timeZoneOffset')}
            validate={[required(), number()]}
            step={1}
          />
        </Grid>
        <Grid item sm={sm} />
      </Grid>
      <Grid container spacing={2}>
        <Grid item sm={sm} style={{ display: 'flex' }}>
          <BooleanInput
            source='externalAccountLinkRegistration.enabled'
            label={t('externalAccountLinkRegistration')}
            onChange={() =>
              [
                resetField('externalAccountLinkRegistration.initialIterationMs'),
                resetField('externalAccountLinkRegistration.regularIterationMs'),
                resetField('externalAccountLinkRegistration.batchSize'),
              ].forEach((f) => f())
            }
          />
        </Grid>
        <Grid item sm={sm} />
      </Grid>
      {isExternalAccountLinkRegistrationEnabled && (
        <>
          <Grid container spacing={2}>
            <Grid item sm={sm}>
              <NumberInput
                source='externalAccountLinkRegistration.regularIterationMs'
                label={t('regularIterationMs')}
                validate={required()}
                parse={NumberEx.positive}
                min={0}
              />
            </Grid>
            <Grid item sm={sm}>
              <NumberInput
                source='externalAccountLinkRegistration.batchSize'
                label={t('batchSize')}
                parse={NumberEx.positive}
                min={0}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item sm={sm}>
              <NumberInput
                source='externalAccountLinkRegistration.initialIterationMs'
                label={t('initialIterationMs')}
                validate={required()}
                parse={NumberEx.positive}
                min={0}
              />
            </Grid>
            <Grid item sm={sm} />
          </Grid>
        </>
      )}
      <Grid container spacing={2}>
        <Grid item sm={sm} style={{ display: 'flex' }}>
          <BooleanInput
            source='accountMirroring.enabled'
            label={t('accountMirroring')}
            onChange={() =>
              [
                resetField('accountMirroring.regularIterationMs'),
                resetField('accountMirroring.batchSize'),
              ].forEach((f) => f())
            }
          />
        </Grid>
        <Grid item sm={sm} />
      </Grid>
      {isAccountMirroringEnabled && (
        <Grid container spacing={2}>
          <Grid item sm={sm}>
            <NumberInput
              source='accountMirroring.regularIterationMs'
              label={t('regularIterationMs')}
              validate={required()}
              parse={NumberEx.positive}
              min={0}
            />
          </Grid>
          <Grid item sm={sm}>
            <NumberInput
              source='accountMirroring.batchSize'
              label={t('batchSize')}
              parse={NumberEx.positive}
              min={0}
            />
          </Grid>
        </Grid>
      )}
      <Grid container spacing={2}>
        <Grid item sm={sm} style={{ display: 'flex' }}>
          <BooleanInput
            source='externalProfileLinkSync.enabled'
            label={t('externalProfileLinkSync')}
            onChange={() =>
              [
                resetField('externalProfileLinkSync.regularIterationMs'),
                resetField('externalProfileLinkSync.batchSize'),
              ].forEach((f) => f())
            }
          />
        </Grid>
        <Grid item sm={sm} />
      </Grid>
      {isExternalProfileLinkSyncEnabled && (
        <Grid container spacing={2}>
          <Grid item sm={sm}>
            <NumberInput
              source='externalProfileLinkSync.regularIterationMs'
              label={t('regularIterationMs')}
              validate={required()}
              parse={NumberEx.positive}
              min={0}
            />
          </Grid>
          <Grid item sm={sm}>
            <NumberInput
              source='externalProfileLinkSync.batchSize'
              label={t('batchSize')}
              parse={NumberEx.positive}
              min={0}
            />
          </Grid>
        </Grid>
      )}
      <Grid container spacing={2}>
        <Grid item sm={sm} style={{ display: 'flex' }}>
          <BooleanInput
            source='transactionTransferMirroring.enabled'
            label={t('transactionTransferMirroring')}
            onChange={() =>
              [
                resetField('transactionTransferMirroring.regularIterationMs'),
                resetField('transactionTransferMirroring.delaySeconds'),
                resetField('transactionTransferMirroring.batchSize'),
              ].forEach((f) => f())
            }
          />
        </Grid>
        <Grid item sm={sm} />
      </Grid>
      {isTransactionTransferMirroringEnabled && (
        <>
          <Grid container spacing={2}>
            <Grid item sm={sm}>
              <NumberInput
                source='transactionTransferMirroring.regularIterationMs'
                label={t('regularIterationMs')}
                validate={required()}
                parse={NumberEx.positive}
                min={0}
              />
            </Grid>
            <Grid item sm={sm}>
              <NumberInput
                source='transactionTransferMirroring.batchSize'
                label={t('batchSize')}
                parse={NumberEx.positive}
                min={0}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item sm={sm}>
              <NumberInput
                source='transactionTransferMirroring.delaySeconds'
                label={t('delaySeconds')}
                validate={[required(), number()]}
                parse={NumberEx.positive}
                min={0}
              />
            </Grid>
            <Grid item sm={sm} />
          </Grid>
          <>
            <H3>{t('transitChannelKeys')}</H3>
            {transitChannelMapping &&
              !isEmpty(transitChannelMapping) &&
              Object.entries(transitChannelMapping)?.map(([currencyCode]) => {
                return (
                  <KeyValueField
                    valueLabel={t('mambuTransitChannel')}
                    valueSource={`transactionTransferMirroring.transitChannelKeys.${currencyCode}`}
                    onDelete={() =>
                      onObjectDelete('transactionTransferMirroring.transitChannelKeys')(
                        currencyCode
                      )
                    }
                  >
                    <TextField label={t('currency')}>{allCurrencies[currencyCode]}</TextField>
                  </KeyValueField>
                );
              })}
            <Button
              aria-controls='simple-menu'
              aria-haspopup='true'
              onClick={() => setOpenTransitChannelDialog(true)}
              color='primary'
              startIcon={<AddIcon />}
            >
              {t('addTransitChannelKeys')}
            </Button>
          </>
          <H3>{t('transitDepositKeys')}</H3>
          {transitDepositMapping &&
            !isEmpty(transitDepositMapping) &&
            Object.entries(transitDepositMapping)?.map(([currencyCode]) => {
              return (
                <KeyValueField
                  valueLabel={t('mambuTransitDeposit')}
                  valueSource={`transactionTransferMirroring.transitDepositKeys.${currencyCode}`}
                  onDelete={() =>
                    onObjectDelete('transactionTransferMirroring.transitDepositKeys')(currencyCode)
                  }
                >
                  <TextField label={t('currency')}>{allCurrencies[currencyCode]}</TextField>
                </KeyValueField>
              );
            })}
          <Button
            aria-controls='simple-menu'
            aria-haspopup='true'
            onClick={() => setOpenTransitDepositDialog(true)}
            color='primary'
            startIcon={<AddIcon />}
          >
            {t('addTransitDepositKeys')}
          </Button>
        </>
      )}
      <H2>{t('clientRoleKeys')}</H2>
      {individualSolutionMapping &&
        !isEmpty(individualSolutionMapping) &&
        Object.entries(individualSolutionMapping)?.map(([veenguIndividualSolutionId]) => {
          return (
            <ReferenceFieldMambu
              key={`${veenguIndividualSolutionId}`}
              source={`clientRoleKeys.${veenguIndividualSolutionId}`}
              mambuObjectLabel={t('mambuClientRoleId')}
              veenguObjectId={veenguIndividualSolutionId}
              veenguObjectLabel={`${t('individualSolution')} ${veenguIndividualSolutionId}`}
              veenguObjectType='SOLUTION'
              onDelete={onObjectDelete('clientRoleKeys')}
            />
          );
        })}
      <Button
        aria-controls='simple-menu'
        aria-haspopup='true'
        onClick={() => setOpenIndividualSolutionDialog(true)}
        color='primary'
        startIcon={<AddIcon />}
      >
        {t('addClientRoleKeys')}
      </Button>
      <H2>{t('groupRoleKeys')}</H2>
      {businessSolutionMapping &&
        !isEmpty(businessSolutionMapping) &&
        Object.entries(businessSolutionMapping)?.map(([veenguBusinessSolutionId]) => {
          return (
            <ReferenceFieldMambu
              key={`${veenguBusinessSolutionId}`}
              source={`groupRoleKeys.${veenguBusinessSolutionId}`}
              mambuObjectLabel={t('mambuGroupRoleId')}
              veenguObjectId={veenguBusinessSolutionId}
              veenguObjectLabel={`${t('businessSolution')} ${veenguBusinessSolutionId}`}
              veenguObjectType='SOLUTION'
              onDelete={onObjectDelete('groupRoleKeys')}
            />
          );
        })}
      <Button
        aria-controls='simple-menu'
        aria-haspopup='true'
        onClick={() => setOpenBusinessSolutionDialog(true)}
        color='primary'
        startIcon={<AddIcon />}
      >
        {t('addGroupRoleKeys')}
      </Button>
      <H2>{t('productTypeKeys')}</H2>
      {productsMapping &&
        !isEmpty(productsMapping) &&
        Object.entries(productsMapping)?.map(([veenguProductId]) => {
          return (
            <ReferenceFieldMambu
              key={`${veenguProductId}`}
              source={`productTypeKeys.${veenguProductId}`}
              mambuObjectLabel={t('mambuProductTypeId')}
              veenguObjectId={veenguProductId}
              veenguObjectLabel={`${t('product')} ${veenguProductId}`}
              veenguObjectType='PRODUCT'
              onDelete={onObjectDelete('productTypeKeys')}
            />
          );
        })}
      <Button
        aria-controls='simple-menu'
        aria-haspopup='true'
        onClick={() => setOpenProductDialog(true)}
        color='primary'
        startIcon={<AddIcon />}
      >
        {t('addProductTypeKeys')}
      </Button>
      <H2>{t('additionalInformation')}</H2>
      <Grid container spacing={2}>
        <Grid item sm={sm}>
          <TextInput source='clientIdPrefix' label={t('clientIdPrefix')} fullWidth />
        </Grid>
        <Grid item sm={sm}>
          <TextInput source='groupIdPrefix' label={t('groupIdPrefix')} fullWidth />
        </Grid>
      </Grid>
      <AddObjectDialog
        veenguObjectValidation={validateObjectAlreadyAdded('productTypeKeys')}
        visible={openProductDialog}
        onClose={() => setOpenProductDialog(false)}
        onEdit={onObjectAdd('productTypeKeys')}
        veenguMappingKey='productTypeKeys'
      />
      <AddObjectDialog
        veenguObjectValidation={validateObjectAlreadyAdded('clientRoleKeys')}
        visible={openIndividualSolutionDialog}
        onClose={() => setOpenIndividualSolutionDialog(false)}
        onEdit={onObjectAdd('clientRoleKeys')}
        veenguMappingKey='clientRoleKeys'
      />
      <AddObjectDialog
        visible={openBusinessSolutionDialog}
        onClose={() => setOpenBusinessSolutionDialog(false)}
        onEdit={onObjectAdd('groupRoleKeys')}
        veenguObjectValidation={validateObjectAlreadyAdded('groupRoleKeys')}
        veenguMappingKey='groupRoleKeys'
      />
      <AddObjectDialog
        visible={openTransitChannelDialog}
        onClose={() => setOpenTransitChannelDialog(false)}
        onEdit={onObjectAdd('transactionTransferMirroring.transitChannelKeys')}
        veenguObjectValidation={validateObjectAlreadyAdded(
          'transactionTransferMirroring.transitChannelKeys'
        )}
        veenguMappingKey='transactionTransferMirroring.transitChannelKeys'
      />
      <AddObjectDialog
        visible={openTransitDepositDialog}
        onClose={() => setOpenTransitDepositDialog(false)}
        onEdit={onObjectAdd('transactionTransferMirroring.transitDepositKeys')}
        veenguObjectValidation={validateObjectAlreadyAdded(
          'transactionTransferMirroring.transitDepositKeys'
        )}
        veenguMappingKey='transactionTransferMirroring.transitDepositKeys'
      />
    </Fragment>
  );
};

const sm = 6;

interface ReferenceFieldMambu {
  veenguObjectType: ObjectType;
  veenguObjectId: string;
  source: string;
  onDelete: (veenguObjectId: string) => void;
  veenguObjectLabel: string;
  mambuObjectLabel: string;
}

const ReferenceFieldMambu: FC<ReferenceFieldMambu> = (props) => {
  const {
    veenguObjectType,
    veenguObjectId,
    onDelete,
    veenguObjectLabel,
    mambuObjectLabel,
    source,
  } = props;
  const classes = useStyles();

  return (
    <KeyValueField
      valueSource={source}
      valueLabel={mambuObjectLabel}
      onDelete={() => onDelete(veenguObjectId)}
    >
      <ReferenceField
        record={{ id: veenguObjectId, type: veenguObjectType }}
        label={veenguObjectLabel}
        className={classes.bottomSpace}
        withLabel
      />
    </KeyValueField>
  );
};

interface AddObjectDialogProps {
  visible: boolean;
  veenguMappingKey: VeenguMappingKeys;
  veenguObjectValidation: () => (veenguUUID: string) => any;
  onClose: () => void;
  onEdit: (newRecord: { veenguId: string; mambuId: string }) => void;
}

const AddObjectDialog = (props: AddObjectDialogProps) => {
  const { visible, onClose, onEdit, veenguObjectValidation, veenguMappingKey } = props;
  const classes = useStyles();

  const translate = useTranslate();
  const t = (key: string): string => translate(l(`fields.${key}`));

  const [veenguObjectLabel, mambuObjectLabel] =
    veenguMappingKey === 'clientRoleKeys'
      ? [t('veenguIndividualSolutionId'), t('mambuClientRoleId')]
      : veenguMappingKey === 'groupRoleKeys'
      ? [t('veenguBusinessSolutionId'), t('mambuGroupRoleId')]
      : veenguMappingKey === 'productTypeKeys'
      ? [t('veenguProductId'), t('mambuProductTypeId')]
      : veenguMappingKey === 'transactionTransferMirroring.transitChannelKeys'
      ? [t('currency'), t('mambuTransitChannel')]
      : veenguMappingKey === 'transactionTransferMirroring.transitDepositKeys'
      ? [t('currency'), t('mambuTransitDeposit')]
      : ['', ''];

  return (
    <Dialog onClose={onClose} aria-labelledby='simple-dialog-title' open={visible}>
      <DialogContent className={classes.dialogContent}>
        <SimpleForm
          redirect={false}
          margin='none'
          className={classes.form}
          toolbar={<BottomToolbar onSave={onEdit} onClose={onClose} />}
        >
          <FormDataConsumer>
            {() => {
              return (
                <>
                  {veenguMappingKey === 'clientRoleKeys' ? (
                    <SolutionSelect
                      source='veenguId'
                      label={veenguObjectLabel}
                      validate={[required(), veenguObjectValidation()]}
                      variant='individual'
                    />
                  ) : veenguMappingKey === 'groupRoleKeys' ? (
                    <SolutionSelect
                      source='veenguId'
                      label={veenguObjectLabel}
                      validate={[required(), veenguObjectValidation()]}
                      variant='business'
                    />
                  ) : veenguMappingKey === 'productTypeKeys' ? (
                    <TextInput
                      source='veenguId'
                      label={veenguObjectLabel}
                      validate={[required(), veenguObjectValidation()]}
                    />
                  ) : veenguMappingKey === 'transactionTransferMirroring.transitChannelKeys' ? (
                    <CurrencyAutocomplete
                      source='veenguId'
                      label={veenguObjectLabel}
                      validate={[required(t('validations.required')), veenguObjectValidation()]}
                      style={{ marginBottom: 4 }}
                    />
                  ) : veenguMappingKey === 'transactionTransferMirroring.transitDepositKeys' ? (
                    <CurrencyAutocomplete
                      source='veenguId'
                      label={veenguObjectLabel}
                      validate={[required(t('validations.required')), veenguObjectValidation()]}
                      style={{ marginBottom: 4 }}
                    />
                  ) : null}
                  <TextInput source='mambuId' label={mambuObjectLabel} validate={required()} />
                </>
              );
            }}
          </FormDataConsumer>
        </SimpleForm>
      </DialogContent>
    </Dialog>
  );
};

type VeenguMappingKeys =
  | 'productTypeKeys'
  | 'clientRoleKeys'
  | 'groupRoleKeys'
  | 'transactionTransferMirroring.transitChannelKeys'
  | 'transactionTransferMirroring.transitDepositKeys';

interface KeyValueFieldProps {
  valueSource: string;
  valueLabel: string;
  onDelete?: () => void;
}

const KeyValueField: FC<PropsWithChildren<KeyValueFieldProps>> = (props) => {
  const { valueSource, valueLabel, onDelete, children } = props;

  const classes = useStyles();
  return (
    <Grid container spacing={2}>
      <Grid item sm={sm} alignItems='center' style={{ display: 'flex' }}>
        {children}
      </Grid>
      <Grid item sm={sm} style={{ display: 'flex' }}>
        <TextInput
          source={valueSource}
          label={valueLabel}
          validate={required()}
          // it is needed for empty text input. otherwise, it is disappears
          parse={(v: any) => v}
          fullWidth
        />
        <IconButton
          type='button'
          size='small'
          onClick={() => onDelete?.()}
          className={cx(classes.actionButton, classes.bottomSpace)}
        >
          <DeleteIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles((theme) => ({
  cardHeader: {
    fontSize: 10,
    fontWeight: 'normal',
    marginBottom: 4,
  },
  AccordionDetails: {
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(3),
    display: 'block',
  },
  actionButton: {
    color: theme.palette.text.primary,
  },
  dialogContent: {
    paddingTop: 0,
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    width: 360,
    boxSizing: 'border-box',
  },
  form: {
    '& > div': {
      padding: 0,
      '&:first-child': {
        paddingTop: 0,
      },
    },
  },
  bottomSpace: {
    marginBottom: theme.spacing(2),
  },
}));

export const l = (key: string): string => `resources.ledgers.mambu.${key}`;

export default ConfigurationForm;
